import { useQuery } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { queryKeys } from '../keys'
import { queryGet, queryAxios } from '../utils/request'

export interface DeviceByType {
  device_type: string
  device_type_label: string
  count: number
}

export interface DeviceDocumentStats {
  device_uuid: string
  device_type: string
  device_type_label: string
  company_id: number
  company_name: string
  nfce_serie: number | null
  document_count: number
  revenue: number | null
  is_disabled: boolean | null
}

export interface BillingCompany {
  company_id: number
  company_name: string
  company_cnpj: string
  active_device_count: number
  devices_by_type: DeviceByType[]
  total_document_count: number
  documents_by_device: DeviceDocumentStats[]
  revenue: number | null
  nfce_count_revenue: number | null
  avg_ticket: number | null
  registered_device_count: number | null
  disabled_device_count: number | null
}

export interface BillingTenant {
  tenant_id: number
  tenant_name: string
  tenant_cnpj: string
  company_count: number
  user_count: number
  active_device_count: number
  devices_by_type: DeviceByType[]
  total_document_count: number
  documents_by_device: DeviceDocumentStats[]
  total_revenue: number | null
  total_nfces_revenue: number | null
  avg_ticket: number | null
  registered_device_count: number | null
  disabled_device_count: number | null
}

export interface BillingMetricsResponse {
  period_start: string
  period_end: string
  generated_at: string
  tenant?: BillingTenant
  companies: BillingCompany[]
}

export interface BillingMetricsListResponse {
  period_start: string
  period_end: string
  generated_at: string
  tenants: BillingTenant[]
}

export interface BillingMetricsFilters {
  date_start: string
  date_end: string
  environment?: string
}

export const useBillingMetricsQuery = (
  tenantId: MaybeRefOrGetter<number | null>,
  filters: MaybeRefOrGetter<BillingMetricsFilters | null>,
) => {
  return useQuery({
    queryKey: computed(() => queryKeys.billingMetrics(toValue(tenantId)!, toValue(filters))),
    queryFn: async (): Promise<BillingMetricsResponse> => {
      const id = toValue(tenantId)
      const params = toValue(filters)
      return queryGet<BillingMetricsResponse>(`/v2/vendor/billing/metrics/${id}`, {
        params: { ...params, environment: params?.environment || 'production' },
      })
    },
    enabled: computed(() => !!toValue(tenantId) && !!toValue(filters)),
  })
}

export const useBillingMetricsListQuery = (
  filters: MaybeRefOrGetter<BillingMetricsFilters | null>,
) => {
  return useQuery({
    queryKey: computed(() => queryKeys.billingMetricsList(toValue(filters))),
    queryFn: async (): Promise<BillingMetricsListResponse> => {
      const params = toValue(filters)
      return queryGet<BillingMetricsListResponse>('/v2/vendor/billing/metrics', {
        params: { ...params, environment: params?.environment || 'production' },
      })
    },
    enabled: computed(() => !!toValue(filters)),
  })
}

export async function exportBillingMetrics(
  tenantId: number,
  format: string,
  params: BillingMetricsFilters,
) {
  const response = await queryAxios.get(`/v2/vendor/billing/metrics/${tenantId}/export/${format}`, {
    params: { ...params, environment: params.environment || 'production' },
    responseType: 'blob',
  })
  return response
}

export async function exportConsolidatedBillingMetrics(
  format: 'pdf' | 'excel',
  params: BillingMetricsFilters & { 'tenant_ids[]'?: number[] },
) {
  const { 'tenant_ids[]': tenantIds, ...rest } = params
  const queryParams: Record<string, any> = { ...rest, environment: rest.environment || 'production' }
  if (tenantIds?.length) {
    queryParams['tenant_ids[]'] = tenantIds
  }
  const response = await queryAxios.get(`/v2/vendor/billing/metrics/export/${format}`, {
    params: queryParams,
    responseType: 'blob',
  })
  return response
}
